/**
 * Fait : le formulaire qui pose UN plafond — celui de la famille entière comme
 *   celui d'une personne. Il montre ce qui est déjà consommé et engagé, lit un
 *   montant écrit à la main, et AVERTIT quand ce montant vaut zéro.
 *
 *   POURQUOI L'AVERTISSEMENT DU ZÉRO, et pourquoi il n'est pas une fenêtre de
 *   confirmation : zéro ne détruit rien, on repose un plafond à la ligne
 *   suivante — et la fenêtre modale du produit est réservée à l'irréversible,
 *   sinon elle s'use et ne protège plus rien. Mais zéro n'est pas non plus « un
 *   petit budget » : il ferme TOUT, y compris les modèles par abonnement qui ne
 *   coûtent pas un centime. C'est cette surprise-là que l'avertissement enlève,
 *   et il s'affiche pendant la saisie, donc avant l'enregistrement.
 *
 * Ne fait pas : il n'appelle pas le serveur — il rend un montant en CENTIMES à
 *   qui l'a ouvert — ne décide d'aucun droit, et ne se souvient de rien : sa
 *   valeur de départ est lue au montage, et l'écran le remonte par sa clé
 *   après chaque enregistrement.
 */

import { useState } from 'react';

import { Annonce } from '../gabarits/Annonce.tsx';
import { Champ } from '../gabarits/Champ.tsx';
import type { BudgetVu } from './api-argent.ts';
import { enCentimes, enEuros, enSaisie } from './montants.ts';

/** Ce que l'on répond à une saisie qui n'est pas un montant. */
const SAISIE_ILLISIBLE = 'Écrivez un montant en euros, par exemple 12,50.';

export function FormulaireDePlafond({
  libelle,
  budget,
  enCours,
  marque,
  surEnvoi,
}: {
  /** Ce que ce plafond borne, dit en toutes lettres — il devient le libellé. */
  readonly libelle: string;

  readonly budget: BudgetVu;
  readonly enCours: boolean;

  /** La marque du formulaire, pour que chaque plafond soit nommé à l'essai. */
  readonly marque: string;

  readonly surEnvoi: (plafondCentimes: number, version: number) => void;
}) {
  const [saisie, setSaisie] = useState(enSaisie(budget.plafondCentimes));
  const [erreur, setErreur] = useState<string | null>(null);

  const lu = enCentimes(saisie);
  const restant = budget.plafondCentimes - budget.consommeCentimes - budget.engageCentimes;

  return (
    <form
      data-role={marque}
      onSubmit={(evenement) => {
        evenement.preventDefault();

        if (lu === null) {
          setErreur(SAISIE_ILLISIBLE);
          return;
        }

        setErreur(null);
        surEnvoi(lu, budget.version);
      }}
    >
      <p data-role={`${marque}-etat`}>
        {budget.pose
          ? `Plafond : ${enEuros(budget.plafondCentimes)}. Déjà dépensé : ` +
            `${enEuros(budget.consommeCentimes)}. Retenu par des demandes en cours : ` +
            `${enEuros(budget.engageCentimes)}. Il reste ${enEuros(restant)}.`
          : 'Aucun plafond n’a jamais été posé ici. Tant qu’il n’y en a pas, rien ne passe : ' +
            'un plafond absent vaut zéro.'}
      </p>

      <Champ
        libelle={libelle}
        aide="En euros. La virgule et le point sont acceptés."
        type="text"
        inputMode="decimal"
        name={marque}
        data-role={`${marque}-montant`}
        value={saisie}
        {...(erreur === null ? {} : { erreur })}
        onChange={(evenement) => {
          setSaisie(evenement.target.value);
          setErreur(null);
        }}
      />

      {lu === 0 && (
        /* `urgent` parce que cet avertissement change la décision qu'on est en
           train de prendre : l'annoncer poliment le ferait lire APRÈS le clic. */
        <Annonce urgence="urgent" nature="refus" data-role={`${marque}-avertissement-zero`}>
          Zéro ferme tout. Plus aucune demande ne partira — y compris vers les modèles par
          abonnement, qui ne coûtent pourtant rien.
        </Annonce>
      )}

      <div className="barre-de-boutons">
        <button type="submit" data-role={`${marque}-enregistrer`} disabled={enCours}>
          {enCours ? 'Enregistrement…' : 'Enregistrer ce plafond'}
        </button>
      </div>
    </form>
  );
}
